'use client';

import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import Image from 'next/image';
import { ArticleTableOfContents } from '@/components/magazine/ArticleTableOfContents';
import { imageAlt } from '@/lib/render/imageAlt';
import { buildImageUrl } from '@/lib/render/imageUrl';
import {
  useOptionalRenderContext,
  useSubstitute,
} from '@/lib/render/RenderContextProvider';

/**
 * The `Inhalt` list beside the hero image, at the position an editor gave this
 * block on the article Template Page (spec 10.03.02.02.06).
 *
 * The entries come from `article.toc`, which the backend derives in the same
 * pass that writes each Collapsible Block's `anchorId` — this block derives
 * nothing, so the links and the anchors cannot disagree (§ AC 4).
 */
export const MagazineTableOfContentsComponent = () => {
  const ctx = useOptionalRenderContext();

  // Inert on any other Page and empty in a Template Page preview: no other
  // route puts an article in the render context.
  if (!ctx?.entities.article) {
    return null;
  }

  const article = ctx.entities.article;
  const toc = article.toc ?? [];
  const heroImage = article.heroImage ?? undefined;
  // CMS media, absolutised against `NEXT_PUBLIC_STRAPI_URL` — never
  // `withAssetPrefix()`, which is for files under `public/`.
  const heroUrl = ctx.imageUrl(heroImage) || buildImageUrl(heroImage);

  if (toc.length === 0 && !heroUrl) {
    return null;
  }

  return (
    <div className="py-8">
      <div
        className={classNames(
          'grid gap-6',
          toc.length > 0 && heroUrl && 'lg:grid-cols-2',
        )}
      >
        <ArticleTableOfContents entries={toc} />
        {heroUrl && <HeroImage src={heroUrl} media={heroImage} />}
      </div>
    </div>
  );
};

/**
 * Intrinsic dimensions come from the media object. Strapi does not guarantee
 * them on every row, and `next/image` needs either both or `fill` — so a row
 * without them falls back to a 16:9 `fill` box rather than dropping the image.
 */
const HeroImage = ({
  src,
  media,
}: {
  src: string;
  media:
    { width?: number; height?: number; alternativeText?: string } | undefined;
}) => {
  const sub = useSubstitute();
  const { width, height } = media ?? {};
  const sizes = '(min-width: 1024px) 50vw, 100vw';
  // The hero illustrates the article rather than decorating it, so Strapi's
  // alt text is used when an editor wrote one; an empty string otherwise
  // marks it decorative rather than inventing copy.
  const alt = sub(imageAlt(media));

  if (width && height) {
    return (
      <Image
        src={src}
        alt={alt}
        width={width}
        height={height}
        sizes={sizes}
        className="w-full rounded-2xl object-cover"
      />
    );
  }
  return (
    <div className="relative aspect-video w-full">
      <Image
        src={src}
        alt={alt}
        fill
        sizes={sizes}
        className="rounded-2xl object-cover"
      />
    </div>
  );
};
